import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useGlobalSearch } from "@/features/backoffice";
import type { GlobalSearchHit } from "@/features/backoffice";
import {
  formatTimestamp,
  timestampTitle,
} from "@/features/backoffice/utils/columns";
import { useDebounce } from "@/features/utils/use-debounce";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The back-office's address bar: one term, every entity.
 *
 * With forty browsable tables, navigating by menu means knowing which table a
 * thing lives in before you can look it up — which is exactly what someone
 * holding a phone number or a session id does not know. So the search fans out
 * server-side and groups the answers by entity.
 *
 * The term is debounced and only fires from two characters: each keystroke
 * otherwise costs one query per table.
 */

/** Where a hit leads — tenants have a page of their own, the rest are rows. */
export const hitPath = (hit: GlobalSearchHit): string =>
  hit.table === "clients"
    ? `/admin/tenants/${hit.id}`
    : `/admin/explorer/${hit.table}/${encodeURIComponent(hit.id)}`;

export function GlobalSearchTrigger() {
  const [open, setOpen] = useState(false);

  // ⌘K / Ctrl-K from anywhere in the back-office.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <Button
        variant="outline"
        className="h-9 w-full max-w-sm justify-start gap-2 px-3 text-muted-foreground font-normal"
        onClick={() => setOpen(true)}
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="truncate">Rechercher partout…</span>
        <kbd className="ml-auto hidden rounded border bg-muted px-1.5 text-[10px] font-medium sm:inline-block">
          ⌘K
        </kbd>
      </Button>
      <GlobalSearchDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function GlobalSearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const debounced = useDebounce(term, 350);
  // Off for every new term: deleted rows are the fallback when the live ones have nothing.
  const [includeDeleted, setIncludeDeleted] = useState(false);
  useEffect(() => setIncludeDeleted(false), [debounced]);
  const { data, isFetching } = useGlobalSearch(
    debounced,
    undefined,
    includeDeleted,
  );

  const groups = data?.groups ?? [];
  const hasResults = groups.some((group) => group.hits.length > 0);

  const go = (path: string) => {
    onOpenChange(false);
    setTerm("");
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl overflow-hidden p-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Recherche globale</DialogTitle>
        {/* `shouldFilter={false}`: the matching already happened server-side,
            across columns the list does not display. Letting cmdk filter again
            on the rendered label would drop exactly the rows found by phone
            number or id — the ones people search for. */}
        <Command shouldFilter={false} className="rounded-lg">
          <CommandInput
            placeholder="Nom, e-mail, téléphone, identifiant…"
            value={term}
            onValueChange={setTerm}
          />
          <CommandList className="max-h-[60vh]">
            {term.trim().length < 2 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Tapez au moins 2 caractères.
              </div>
            ) : isFetching && !hasResults ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" />
                Recherche…
              </div>
            ) : !hasResults ? (
              includeDeleted ? (
                <CommandEmpty>
                  Aucun résultat, même parmi les éléments supprimés.
                </CommandEmpty>
              ) : (
                <div className="flex flex-col items-center gap-3 py-8 text-sm text-muted-foreground">
                  Aucun résultat.
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIncludeDeleted(true)}
                  >
                    Chercher aussi parmi les éléments supprimés
                  </Button>
                </div>
              )
            ) : (
              groups.map((group) => (
                <CommandGroup
                  key={group.table}
                  heading={
                    <span className="flex items-center gap-2">
                      {group.label}
                      <Badge variant="secondary" className="text-[10px]">
                        {group.total}
                      </Badge>
                    </span>
                  }
                >
                  {group.hits.map((hit) => (
                    <CommandItem
                      key={`${hit.table}:${hit.id}`}
                      value={`${hit.table}:${hit.id}`}
                      onSelect={() => go(hitPath(hit))}
                      className="flex items-center gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {hit.title || hit.id}
                          {hit.row?.is_deleted === true && (
                            <Badge
                              variant="outline"
                              color="red"
                              className="ml-2 text-[10px]"
                            >
                              supprimé
                            </Badge>
                          )}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[hit.subtitle, hit.id].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {hit.created_at && (
                        <span
                          className="shrink-0 text-[10px] text-muted-foreground"
                          title={timestampTitle(hit.created_at)}
                        >
                          {formatTimestamp(hit.created_at)}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                  {group.total > group.hits.length && (
                    <CommandItem
                      value={`more:${group.table}`}
                      onSelect={() =>
                        go(
                          `/admin/explorer/${group.table}?q=${encodeURIComponent(
                            debounced,
                          )}`,
                        )
                      }
                      className="text-xs text-muted-foreground"
                    >
                      Voir les {group.total} résultats dans {group.label}
                    </CommandItem>
                  )}
                </CommandGroup>
              ))
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
